import { useEffect, useState, type ReactNode } from "react";
import { Lucide } from "@react-native-vector-icons/lucide";
import { usePathname, useRouter } from "expo-router";
import {
  Modal,
  Platform,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

import { makeStylesheet } from "~/components/AppShell.styles";
import { AppText, BrandMark, Button, ButtonText, Stack } from "~/components/ui";
import { useAuth, type AuthUser } from "~/hooks/useAuth";
import { usePlan } from "~/hooks/usePlan";
import { useSyncStatus } from "~/state/sync-status";
import { useTheme, useThemedStyles } from "~/styles/theme";
import { openExternalUrl } from "~/utils/external-link";
import { getExternalLinks } from "~/utils/runtime-config";
import { SUBSCRIPTION_PATH } from "~/utils/onboarding";

const navItems = [
  { href: "/collections", label: "Collections", icon: "library" },
  { href: "/plants", label: "All plants", icon: "leaf" },
  { href: "/discover", label: "Botanical index", icon: "search" },
] as const;

function parentRoute(pathname: string) {
  if (/^\/(collections|plants)\/[^/]+$/.test(pathname)) {
    return pathname.replace(/\/[^/]+$/, "");
  }
  if (/^\/collections\/[^/]+\/map$/.test(pathname)) {
    return pathname.replace(/\/map$/, "");
  }
  return null;
}

function userInitials(user: AuthUser) {
  const initials = [user.firstName, user.lastName]
    .filter(Boolean)
    .map((part) => part.trim().charAt(0).toUpperCase())
    .join("");

  return initials || user.email.charAt(0).toUpperCase();
}

function SyncPill() {
  const stylesheet = useThemedStyles(makeStylesheet);
  const status = useSyncStatus();
  const dotStyle =
    status.state === "offline"
      ? stylesheet.syncDotError
      : status.state === "syncing" || status.state === "loading"
        ? stylesheet.syncDotPending
        : null;

  return (
    <View
      accessibilityRole="summary"
      accessibilityLabel={`${status.label}. ${status.hint}.`}
      style={stylesheet.syncPill}
    >
      <View style={[stylesheet.syncDot, dotStyle]} />
      <View style={stylesheet.syncCopy}>
        <Text numberOfLines={1} style={stylesheet.syncLabel}>
          {status.label}
        </Text>
        <Text numberOfLines={1} style={stylesheet.syncHint}>
          {status.hint}
        </Text>
      </View>
    </View>
  );
}

function Navigation({
  user,
  logoutPending,
  isPro,
  onLogoutRequest,
  onNavigate,
}: {
  user: AuthUser | null;
  logoutPending: boolean;
  isPro: boolean;
  onLogoutRequest: () => void;
  onNavigate?: () => void;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const externalLinks = getExternalLinks();
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStylesheet);

  return (
    <View style={stylesheet.sidebarBody}>
      <View style={stylesheet.sidebarBrand}>
        <BrandMark size="md" />
      </View>
      <SyncPill />
      <Stack gap="xs">
        <Text style={stylesheet.navSectionLabel}>Catalogue</Text>
        {navItems.map((item) => {
          const active =
            pathname === item.href || pathname.startsWith(`${item.href}/`);
          return (
            <Pressable
              key={item.href}
              accessibilityRole="link"
              accessibilityLabel={item.label}
              accessibilityState={{ selected: active }}
              onPress={() => {
                router.push(item.href as never);
                onNavigate?.();
              }}
              style={({ hovered, pressed }) => [
                stylesheet.navItem,
                active ? stylesheet.navItemActive : null,
                hovered || pressed ? stylesheet.navItemHover : null,
              ]}
            >
              <Lucide
                name={item.icon}
                size={17}
                color={active ? colors.primary : colors.mutedForeground}
              />
              <Text
                style={[
                  stylesheet.navItemText,
                  active ? stylesheet.navItemTextActive : null,
                ]}
              >
                {item.label}
              </Text>
            </Pressable>
          );
        })}
        <View style={stylesheet.sidebarDivider} />
        <Text style={stylesheet.navSectionLabel}>Account</Text>
        <Pressable
          accessibilityRole="link"
          accessibilityLabel="Settings"
          accessibilityState={{ selected: pathname === "/settings" }}
          onPress={() => {
            router.push("/settings" as never);
            onNavigate?.();
          }}
          style={({ hovered, pressed }) => [
            stylesheet.sidebarUtilityLink,
            pathname === "/settings" ? stylesheet.navItemActive : null,
            hovered || pressed ? stylesheet.navItemHover : null,
          ]}
        >
          <Text
            style={[
              stylesheet.sidebarUtilityText,
              pathname === "/settings"
                ? stylesheet.sidebarUtilityTextActive
                : null,
            ]}
          >
            Settings
          </Text>
        </Pressable>
        <Pressable
          accessibilityRole="link"
          accessibilityState={{ selected: pathname === SUBSCRIPTION_PATH }}
          onPress={() => {
            router.push(SUBSCRIPTION_PATH as never);
            onNavigate?.();
          }}
          style={({ hovered, pressed }) => [
            stylesheet.sidebarUtilityLink,
            pathname === SUBSCRIPTION_PATH ? stylesheet.navItemActive : null,
            hovered || pressed ? stylesheet.navItemHover : null,
          ]}
        >
          <View style={stylesheet.utilityContent}>
            <Text
              style={[
                stylesheet.sidebarUtilityText,
                pathname === SUBSCRIPTION_PATH
                  ? stylesheet.sidebarUtilityTextActive
                  : null,
              ]}
            >
              {isPro ? "Subscription" : "Upgrade to Pro"}
            </Text>
            <View style={stylesheet.planBadge}>
              <Text style={stylesheet.planBadgeText}>
                {isPro ? "PRO" : "FREE"}
              </Text>
            </View>
          </View>
        </Pressable>
        {externalLinks.help ? (
          <Pressable
            accessibilityRole="link"
            onPress={() => void openExternalUrl(externalLinks.help)}
            style={({ hovered, pressed }) => [
              stylesheet.sidebarUtilityLink,
              hovered || pressed ? stylesheet.navItemHover : null,
            ]}
          >
            <Text style={stylesheet.sidebarUtilityText}>Help</Text>
          </Pressable>
        ) : null}
        <Button
          variant="link"
          align="start"
          iconName="log-out"
          onPress={onLogoutRequest}
          disabled={logoutPending}
        >
          <ButtonText>
            {logoutPending ? "Signing out..." : "Sign out"}
          </ButtonText>
        </Button>
      </Stack>

      <View style={{ flex: 1 }} />
      <View style={stylesheet.sidebarFooter}>
        {user ? (
          <Pressable
            accessibilityRole="link"
            accessibilityLabel="Open profile settings"
            onPress={() => {
              router.push("/settings" as never);
              onNavigate?.();
            }}
            style={({ hovered, pressed }) => [
              stylesheet.sidebarProfile,
              hovered || pressed ? stylesheet.navItemHover : null,
            ]}
          >
            <View style={stylesheet.sidebarAvatar}>
              <Text style={stylesheet.sidebarAvatarText}>
                {userInitials(user)}
              </Text>
            </View>
            <Text numberOfLines={1} style={stylesheet.sidebarProfileName}>
              {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                user.email}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function titleCase(value: string) {
  return value
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The last readable path segment: a trailing id (collections/<uuid>) isn't a
 * title, so name the section instead of showing "4a29d889 84e1 4817…". */
function lastReadableSegment(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  while (segments.length > 1 && UUID_SEGMENT.test(segments.at(-1) ?? "")) {
    segments.pop();
  }
  return segments.at(-1);
}

function breadcrumbsFor(pathname: string) {
  const label = lastReadableSegment(pathname);
  return [{ label: label || "Florafolio" }];
}

function pageTitleFor(pathname: string) {
  const breadcrumbs = breadcrumbsFor(pathname);
  return `${titleCase(breadcrumbs.at(-1)?.label ?? "Florafolio")} | Florafolio`;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { width } = useWindowDimensions();
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStylesheet);
  const pathname = usePathname();
  const router = useRouter();
  const { user, logout, logoutPending } = useAuth();
  const plan = usePlan();
  const desktop = width >= 768;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const backRoute = Platform.OS === "web" ? null : parentRoute(pathname);
  const breadcrumbs = breadcrumbsFor(pathname);
  const nearingPlantLimit =
    !plan.isPro &&
    plan.plants.remaining !== null &&
    plan.plants.remaining <= 2 &&
    pathname !== SUBSCRIPTION_PATH;

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    document.title = pageTitleFor(pathname);
  }, [pathname]);

  return (
    <View style={stylesheet.appShell}>
      {desktop ? (
        <View style={stylesheet.sidebar}>
          <Navigation
            user={user}
            logoutPending={logoutPending}
            isPro={plan.isPro}
            onLogoutRequest={() => setLogoutConfirmOpen(true)}
          />
        </View>
      ) : null}

      <View style={stylesheet.appShellContent}>
        {nearingPlantLimit ? (
          <View style={stylesheet.upgradeBanner}>
            <AppText size="xs">
              {plan.plants.remaining === 0
                ? "You've used all 10 free plants. Upgrade to keep cataloguing."
                : `${plan.plants.remaining} free plant ${plan.plants.remaining === 1 ? "slot" : "slots"} left.`}
            </AppText>
            <Pressable
              accessibilityRole="link"
              accessibilityLabel="See Florafolio Pro"
              onPress={() => router.push(SUBSCRIPTION_PATH as never)}
            >
              <AppText size="xs" weight="semibold">
                See Pro →
              </AppText>
            </Pressable>
          </View>
        ) : null}
        <View style={stylesheet.shellHeader}>
          <View style={stylesheet.shellHeaderLeft}>
            {!desktop ? (
              <Button
                variant="outline"
                compact
                onPress={() => setDrawerOpen(true)}
              >
                <ButtonText>Menu</ButtonText>
              </Button>
            ) : null}
            {backRoute ? (
              <Button
                variant="outline"
                compact
                onPress={() => router.replace(backRoute as never)}
              >
                <ButtonText>Back</ButtonText>
              </Button>
            ) : null}
            {!desktop ? <BrandMark size="sm" /> : null}
            <View style={stylesheet.breadcrumbs}>
              {breadcrumbs.map((breadcrumb, index) => (
                <View
                  key={`${breadcrumb.label}-${index}`}
                  style={stylesheet.breadcrumbItem}
                >
                  {index > 0 ? (
                    <Text style={stylesheet.breadcrumbSeparator}>{" / "}</Text>
                  ) : null}
                  <Text numberOfLines={1} style={stylesheet.breadcrumbCurrent}>
                    {titleCase(breadcrumb.label)}
                  </Text>
                </View>
              ))}
            </View>
          </View>
          <View style={stylesheet.shellActions}>
            <Pressable
              accessibilityRole="link"
              accessibilityLabel="Search the botanical index"
              onPress={() => router.push("/discover" as never)}
              style={({ hovered, pressed }) => [
                stylesheet.headerIconButton,
                hovered || pressed ? stylesheet.headerIconButtonHover : null,
              ]}
            >
              <Lucide name="search" size={18} color={colors.heading} />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Support"
              onPress={() => router.push("/support" as never)}
              style={({ hovered, pressed }) => [
                stylesheet.headerIconButton,
                hovered || pressed ? stylesheet.headerIconButtonHover : null,
              ]}
            >
              <Lucide name="megaphone" size={18} color={colors.heading} />
            </Pressable>
          </View>
        </View>
        {children}
      </View>

      <Modal
        animationType="slide"
        transparent
        visible={drawerOpen}
        onRequestClose={() => setDrawerOpen(false)}
      >
        <View style={stylesheet.drawerOverlay}>
          <View style={stylesheet.drawerPanel}>
            <Navigation
              user={user}
              logoutPending={logoutPending}
              isPro={plan.isPro}
              onLogoutRequest={() => {
                setDrawerOpen(false);
                setLogoutConfirmOpen(true);
              }}
              onNavigate={() => setDrawerOpen(false)}
            />
          </View>
          <Pressable
            accessibilityLabel="Close navigation"
            style={stylesheet.drawerDismiss}
            onPress={() => setDrawerOpen(false)}
          />
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={logoutConfirmOpen}
        onRequestClose={() => setLogoutConfirmOpen(false)}
      >
        <View style={stylesheet.modalOverlay}>
          <View style={stylesheet.confirmationCard}>
            <View>
              <Text style={stylesheet.modalTitle}>Sign out?</Text>
              <AppText>
                Plants saved on this device stay here until you sign back in.
              </AppText>
            </View>
            <View style={stylesheet.confirmationActions}>
              <Button
                variant="outline"
                compact
                onPress={() => setLogoutConfirmOpen(false)}
                disabled={logoutPending}
              >
                <ButtonText>Cancel</ButtonText>
              </Button>
              <Button
                compact
                onPress={() => void logout()}
                disabled={logoutPending}
              >
                <ButtonText>
                  {logoutPending ? "Signing out..." : "Sign out"}
                </ButtonText>
              </Button>
            </View>
          </View>
        </View>
      </Modal>

    </View>
  );
}
